// src/pages/SubcodonProfile/BatchSubcodonProfile.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button, ButtonGroup, Alert } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";

// Common Components (Our new reusable components)
import GeneralSettings from "../../Components/Common/GeneralSettings";
import Files from "../../Components/Common/Files";
import SelectedFilesDisplay from "../../Components/Common/SelectedFilesDisplay";
import PlotContainer from "../../Components/Common/PlotContainer";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ActionButton from "../../Components/Common/ActionButton";

// Batch-specific components
import BatchTranscriptList from "./components/BatchTranscriptList";
import BatchOutputOptions from "./components/BatchOutputOptions";
import BatchJobStatus from "./components/BatchJobStatus";

// API
import {
  getBatchProfileData,
  generateBatchSubcodonPlot,
  getPlusStatus,
} from "../../helpers/eirna_backend_helper";

// Hooks (Our new reusable hooks)
import { useSettings } from "../../hooks/useSettings";
import { useFileSelection } from "../../hooks/useFileSelection";
import { useSmartReadLength } from "../../hooks/useSmartReadLength";
import { useBatchSettings } from "./hooks/useBatchSettings";

// Configuration (Our new config-driven approach)
import {
  subcodonProfileConfig,
  batchSubcodonProfileDefaults,
  batchSubcodonProfileValidation,
} from "../../config/plotConfigs/subcodonProfileConfig";

// Short code handling
import {
  useBatchSubcodonProfileShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";

import { FadeLoader } from "react-spinners";

const BatchSubcodonProfile = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [loading, setLoading] = useState(true);
  const [plotLoading, setPlotLoading] = useState(false);
  const [plusStatus, setPlusStatus] = useState(null);
  const [isBatchMode, setIsBatchMode] = useState(true);
  const [error, setError] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);

  // API data
  const [apiData, setApiData] = useState({
    json_files: [],
    default_tran: "",
    seq_types: [],
    studies_dict: {},
    subgroup_present: false,
  });

  // Enhanced hooks with reusable components
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = useSettings(batchSubcodonProfileDefaults, batchSubcodonProfileValidation);

  const {
    selectedFiles,
    selectedSeqType,
    selectedGroup,
    toggleFile,
    toggleAllFiles,
    setSelectedSeqType,
    setSelectedGroup,
    clearSelection,
    selectMultipleFiles,
    deselectMultipleFiles,
    setSelectedFiles,
  } = useFileSelection("riboseq");

  const {
    getStandardReadLengths,
    markFieldAsUserModified,
    resetUserModifications,
    shouldAutoAdjust,
  } = useSmartReadLength();

  const { batchSettings, updateBatchSetting, resetBatchSettings } =
    useBatchSettings();

  // Use the Batch Subcodon Profile specific short code handler
  const { userPassedShortCode, autoRunPlot, setAutoRunPlot } =
    useBatchSubcodonProfileShortCode({
      location,
      updateMultipleSettings,
      setSelectedFiles,
      updateBatchSetting,
    });

  // Check Connect+ status before loading the rest of the page
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const plus_status_response = await getPlusStatus(projectId);
        setPlusStatus(plus_status_response.plus_status);
      } catch (err) {
        setError("Failed to check Connect+ access");
        setPlusStatus(false);
      }
    };

    if (projectId) {
      fetchStatus();
    }
  }, [projectId]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getBatchProfileData(projectId);
        setApiData(response);

        // Set default transcript
        if (response.default_tran) {
          updateSetting("transcript", response.default_tran);
        }

        // Set default seq type with preference for riboseq
        const fileTypes = [
          ...new Set(response.json_files?.map((file) => file.file_type) || []),
        ];
        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }

        const uniqueGroups = [
          ...new Set(
            response.json_files?.map((file) => file.subgroup).filter(Boolean) ||
              []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }
      } catch (err) {
        setError("Failed to load project data");
        toast.error("Failed to load project data");
      } finally {
        setLoading(false);
      }
    };

    if (projectId && plusStatus === true) {
      fetchData();
    }
  }, [
    projectId,
    plusStatus,
    updateSetting,
    setSelectedSeqType,
    setSelectedGroup,
  ]);

  // Auto-trigger batch generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot) {
      console.log("Auto-run triggered, checking conditions...", {
        autoRunPlot,
        plotLoading,
        loading,
        selectedFilesCount: selectedFiles.length,
        csvCheck: batchSettings.csvCheck,
        htmlCheck: batchSettings.htmlCheck,
      });

      if (
        !plotLoading &&
        !loading &&
        selectedFiles.length > 0 &&
        (batchSettings.csvCheck || batchSettings.htmlCheck)
      ) {
        console.log(
          "All conditions met, generating batch files from short code..."
        );
        setAutoRunPlot(false);
        setTimeout(() => {
          handleGenerateBatchPlot();
        }, 50);
      } else {
        console.log(
          "Conditions not met yet, will retry when conditions change"
        );
      }
    }
  }, [
    autoRunPlot,
    plotLoading,
    loading,
    selectedFiles.length,
    batchSettings.csvCheck,
    batchSettings.htmlCheck,
    setAutoRunPlot,
  ]);

  // Filter files based on selected seq type and group
  const filteredFiles = useMemo(() => {
    let files = apiData.json_files || [];
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((file) => file.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((file) => file.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  // Get unique seq types and groups
  const seqTypes = useMemo(() => {
    const types = [
      ...new Set(apiData.json_files?.map((file) => file.file_type) || []),
    ];
    return types.map((type) => ({
      value: type,
      label: type,
      count:
        apiData.json_files?.filter((file) => file.file_type === type).length ||
        0,
    }));
  }, [apiData.json_files]);

  const groups = useMemo(() => {
    const groupSet = new Set(
      apiData.json_files?.map((file) => file.subgroup) || []
    );
    return Array.from(groupSet)
      .filter((group) => group)
      .map((group) => ({
        value: group,
        label: group,
        count:
          apiData.json_files?.filter((file) => file.subgroup === group)
            .length || 0,
      }));
  }, [apiData.json_files]);

  // Check if all files of selected seq type are selected
  const allSeqTypeFilesSelected = useMemo(() => {
    if (!selectedSeqType || selectedSeqType === "all") return false;
    const seqTypeFiles = (apiData.json_files || []).filter(
      (file) => file.file_type === selectedSeqType
    );
    return (
      seqTypeFiles.length > 0 &&
      seqTypeFiles.every((file) => selectedFiles.includes(file.file_id))
    );
  }, [selectedSeqType, apiData.json_files, selectedFiles]);

  // Time estimate calculation
  const timeEstimate = useMemo(() => {
    const transcriptCount = batchSettings.transcriptList.trim()
      ? batchSettings.transcriptList
          .split("\n")
          .map((t) => t.trim())
          .filter(Boolean).length
      : 0;
    const sampleCount = selectedFiles.length;
    const hasCsv = batchSettings.csvCheck;
    const hasHtml = batchSettings.htmlCheck;

    if (transcriptCount === 0 || sampleCount === 0 || (!hasCsv && !hasHtml)) {
      return null;
    }

    let totalSeconds = 0;
    if (hasCsv) {
      const csvBase = 0.002;
      const csvPerSample = 0.001;
      totalSeconds += (csvBase + csvPerSample * sampleCount) * transcriptCount;
    }
    if (hasHtml) {
      const htmlBase = 0.17;
      const htmlPerSample = 0.01;
      totalSeconds += (htmlBase + htmlPerSample * sampleCount) * transcriptCount;
    }

    const minutes = Math.ceil(totalSeconds / 60);
    return { minutes, transcriptCount, sampleCount, hasCsv, hasHtml };
  }, [
    batchSettings.transcriptList,
    batchSettings.csvCheck,
    batchSettings.htmlCheck,
    selectedFiles.length,
  ]);

  // Check if all files of selected group are selected
  const allGroupFilesSelected = useMemo(() => {
    if (!selectedGroup || selectedGroup === "all") return false;
    const groupFiles = filteredFiles.filter(
      (file) => file.subgroup === selectedGroup
    );
    return (
      groupFiles.length > 0 &&
      groupFiles.every((file) => selectedFiles.includes(file.file_id))
    );
  }, [selectedGroup, filteredFiles, selectedFiles]);

  // Enhanced setting update handler with smart adjustment tracking
  const handleSettingUpdate = useCallback(
    (key, value) => {
      updateSetting(key, value);
      markFieldAsUserModified(key);
    },
    [updateSetting, markFieldAsUserModified]
  );

  // Enhanced reset handler
  const handleReset = useCallback(() => {
    resetSettings();
    resetBatchSettings();
    resetUserModifications();
    toast.info("Settings reset to defaults");
  }, [resetSettings, resetBatchSettings, resetUserModifications]);

  // Handle select all seq type files
  const handleSelectAllSeqTypeFiles = useCallback(
    (checked) => {
      if (!selectedSeqType || selectedSeqType === "all") return;
      const seqTypeFiles = (apiData.json_files || []).filter(
        (file) => file.file_type === selectedSeqType
      );
      const seqTypeFileIds = seqTypeFiles.map((file) => file.file_id);

      if (checked) {
        selectMultipleFiles(seqTypeFileIds);
      } else {
        deselectMultipleFiles(seqTypeFileIds);
      }
    },
    [
      selectedSeqType,
      apiData.json_files,
      selectMultipleFiles,
      deselectMultipleFiles,
    ]
  );

  // Handle select all group files
  const handleSelectAllGroupFiles = useCallback(
    (checked) => {
      if (!selectedGroup || selectedGroup === "all") return;
      const groupFiles = filteredFiles.filter(
        (file) => file.subgroup === selectedGroup
      );
      const groupFileIds = groupFiles.map((file) => file.file_id);

      if (checked) {
        selectMultipleFiles(groupFileIds);
      } else {
        deselectMultipleFiles(groupFileIds);
      }
    },
    [selectedGroup, filteredFiles, selectMultipleFiles, deselectMultipleFiles]
  );

  const handleModeToggle = useCallback(
    (mode) => {
      if (mode === "single") {
        navigate(`/projects/${projectId}/interactive_plot/`, {
          state: {
            currentSettings: settings,
            selectedFiles: selectedFiles,
            selectedSeqType: selectedSeqType,
            selectedGroup: selectedGroup,
          },
        });
      } else {
        setIsBatchMode(true);
      }
    },
    [
      navigate,
      projectId,
      settings,
      selectedFiles,
      selectedSeqType,
      selectedGroup,
    ]
  );
  const handleGenerateBatchPlot = useCallback(async () => {
    if (plotLoading) {
      console.log("Batch plot generation already in progress, skipping...");
      return;
    }

    const validation = validateSettings();
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    if (selectedFiles.length === 0) {
      toast.error("Please select at least one file");
      return;
    }

    // Check if transcript list is provided
    if (!batchSettings.transcriptList.trim()) {
      toast.error(
        "Please provide at least one transcript for batch processing"
      );
      return;
    }

    // Check if at least one output format is selected
    if (!batchSettings.csvCheck && !batchSettings.htmlCheck) {
      toast.error("Please select at least one output format (CSV or HTML)");
      return;
    }

    try {
      setPlotLoading(true);
      setError(null);
      setJobStatus(null);

      // Show different message for auto-trigger vs manual trigger
      if (userPassedShortCode) {
        toast.info("Auto-generating batch files from shared link...");
      }

      // Use the Batch Subcodon Profile specific payload creator
      const plotRequest = createPlotSpecificPayload.batchSubcodonProfile({
        settings,
        selectedFiles,
        projectId,
        userPassedShortCode,
        batchSettings,
      });

      console.log("Batch plot request:", plotRequest);

      const response = await generateBatchSubcodonPlot(plotRequest);

      if (response.message) {
        if (response.message === "user logged out") {
          navigate("/login");
          return;
        }
        setError(response.message);
        toast.error(response.message);
        setPlotLoading(false);
      } else if (response.task_id) {
        setJobStatus({
          taskId: response.task_id,
          status: "running",
          message: `Job started with ID: ${response.task_id}`,
        });

        if (userPassedShortCode) {
          toast.success("Batch job from shared link started successfully!");
        } else {
          toast.success("Batch job started successfully");
        }

        setPlotLoading(false);
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.message || "Failed to generate batch plot";
      setError(errorMsg);
      toast.error(errorMsg);
      setPlotLoading(false);
    }
  }, [
    settings,
    selectedFiles,
    projectId,
    batchSettings,
    validateSettings,
    navigate,
    plotLoading,
    userPassedShortCode,
  ]);

  const handleJobStatusUpdate = useCallback((newStatus) => {
    setJobStatus(newStatus);
  }, []);

  if (plusStatus === null || (plusStatus === true && loading)) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#1A6D52" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (plusStatus === false) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="info" className="mb-0">
            This plot is part of Connect+ To discuss access please contact info@eirnabio.com
          </Alert>
        </Container>
      </div>
    );
  }

  document.title = "Batch Sub-codon Profile | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Batch Subcodon Position Profile" },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <h4
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0",
                  }}
                >
                  Batch SubCodon Position Profile
                  {autoRunPlot && (
                    <span
                      className="badge bg-info ms-2"
                      style={{ fontSize: "12px" }}
                    >
                      Auto-generating...
                    </span>
                  )}
                </h4>

                {/* Mode Toggle */}
                <ButtonGroup>
                  <Button
                    color={!isBatchMode ? "success" : "light"}
                    onClick={() => handleModeToggle("single")}
                    style={{
                      backgroundColor: !isBatchMode ? "#1a6d51" : "#f8f9fa",
                      borderColor: !isBatchMode ? "#1a6d51" : "#d1d5db",
                      color: !isBatchMode ? "white" : "#6b7280",
                      fontWeight: "500",
                    }}
                  >
                    <i className="ri-user-line me-1"></i>
                    Single Mode
                  </Button>
                  <Button
                    color={isBatchMode ? "success" : "light"}
                    onClick={() => handleModeToggle("batch")}
                    style={{
                      backgroundColor: isBatchMode ? "#1a6d51" : "#f8f9fa",
                      borderColor: isBatchMode ? "#1a6d51" : "#d1d5db",
                      color: isBatchMode ? "white" : "#6b7280",
                      fontWeight: "500",
                    }}
                  >
                    <i className="ri-stack-line me-1"></i>
                    Batch Mode
                  </Button>
                </ButtonGroup>
              </div>
            </Col>
          </Row>

          {/* Top Row - General Settings and Seq Types/Groups */}
          <Row className="mb-4">
            {/* Left Card - General Settings */}
            <Col lg={12}>
              <GeneralSettings
                settings={settings}
                onUpdateSetting={handleSettingUpdate}
                onReset={handleReset}
                config={subcodonProfileConfig}
                title="General Settings"
                loading={plotLoading}
                selectedFiles={selectedFiles}
                mode="batch"
              />
            </Col>
          </Row>
          <Row className="mb-4">
            {/* Right Card - Seq Types and Groups */}
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={seqTypes}
                groups={groups}
                selectedSeqType={selectedSeqType}
                selectedGroup={selectedGroup}
                onSelectSeqType={setSelectedSeqType}
                onSelectGroup={setSelectedGroup}
                onSelectAllSeqTypeFiles={handleSelectAllSeqTypeFiles}
                onSelectAllGroupFiles={handleSelectAllGroupFiles}
                selectedFiles={selectedFiles}
                filteredFiles={filteredFiles}
                allSeqTypeFilesSelected={allSeqTypeFilesSelected}
                allGroupFilesSelected={allGroupFilesSelected}
                showSelectAllCheckboxes={true}
                customTitle="Seq Types & Groups"
                customIcon="ri-tools-fill"
              />
            </Col>
          </Row>

          {/* Files Card */}
          <Row className="mb-3">
            <Col lg={12}>
              <Files
                files={filteredFiles}
                selectedFiles={selectedFiles}
                onToggleFile={toggleFile}
                onSelectAllGroupFiles={handleSelectAllGroupFiles}
                title="Select Files"
                searchable={true}
                selectable={true}
                filesPerPage={30}
                columns={6}
                showFileCount={true}
                customSearchFields={["file_name", "file_description"]}
              />
            </Col>
          </Row>
          <Row>
            <Col lg={12}>
              <SelectedFilesDisplay
                selectedFiles={selectedFiles}
                allFiles={apiData.json_files}
                title="Selected Files for  Batch Subcodon Profile"
                showSearch={true}
                maxDisplayFiles={12}
              />
            </Col>
          </Row>

          {/* Batch-specific Options */}
          <Row className="mb-4">
            {/* Transcript List */}
            <Col lg={6}>
              <BatchTranscriptList
                transcriptList={batchSettings.transcriptList}
                onUpdateTranscriptList={(value) =>
                  updateBatchSetting("transcriptList", value)
                }
              />
            </Col>

            {/* Output Options */}
            <Col lg={6}>
              <BatchOutputOptions
                csvCheck={batchSettings.csvCheck}
                htmlCheck={batchSettings.htmlCheck}
                onUpdateCsvCheck={(value) =>
                  updateBatchSetting("csvCheck", value)
                }
                onUpdateHtmlCheck={(value) =>
                  updateBatchSetting("htmlCheck", value)
                }
              />
            </Col>
          </Row>

          {/* Time Estimate */}
          {timeEstimate && !jobStatus && (
            <Row className="mb-3">
              <Col lg={12}>
                <div
                  style={{
                    backgroundColor: "#f0faf5",
                    border: "1px solid #a7d7c0",
                    borderRadius: "8px",
                    padding: "14px 20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <i
                    className="ri-time-line"
                    style={{ fontSize: "20px", color: "#1a6d51", flexShrink: 0 }}
                  ></i>
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        fontWeight: "600",
                        color: "#1a6d51",
                        fontSize: "14px",
                      }}
                    >
                      Estimated job time:&nbsp;
                    </span>
                    <span
                      style={{
                        fontWeight: "700",
                        color: "#1f2937",
                        fontSize: "14px",
                      }}
                    >
                      {timeEstimate.minutes === 1
                        ? "~1 minute"
                        : `~${timeEstimate.minutes} minutes`}
                    </span>
                    <span
                      style={{
                        color: "#6b7280",
                        fontSize: "13px",
                        marginLeft: "10px",
                      }}
                    >
                      ({timeEstimate.transcriptCount}{" "}
                      {timeEstimate.transcriptCount === 1
                        ? "transcript"
                        : "transcripts"}
                      , {timeEstimate.sampleCount}{" "}
                      {timeEstimate.sampleCount === 1 ? "sample" : "samples"}
                      {timeEstimate.hasCsv && timeEstimate.hasHtml
                        ? ", CSV + HTML"
                        : timeEstimate.hasCsv
                        ? ", CSV"
                        : ", HTML"}
                      )
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Generate Batch Plot Button */}
          <Row className="mb-3">
            <Col lg={12} className="text-end">
              <ActionButton
                onClick={handleGenerateBatchPlot}
                loading={plotLoading}
                buttonType="plot"
                customText="Generate Files"
              />
            </Col>
          </Row>

          {/* Job Status */}
          {jobStatus && (
            <Row className="mb-3">
              <Col lg={12}>
                <BatchJobStatus
                  jobStatus={jobStatus}
                  onUpdateJobStatus={handleJobStatusUpdate}
                  projectId={projectId}
                />
              </Col>
            </Row>
          )}

          {/* Error Display */}
          {error && !jobStatus && (
            <Row>
              <Col lg={12}>
                <Alert color="danger">
                  <i className="ri-error-warning-line me-2"></i>
                  {error}
                </Alert>
              </Col>
            </Row>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default BatchSubcodonProfile;
